import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import '../css/MessageInput.css'; // Make sure to import the CSS file
import { library } from '@fortawesome/fontawesome-svg-core';
import { faMicrophone, faPaperPlane, faStop, faVolumeUp, faVolumeMute } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios from 'axios';
import { isMobile } from 'react-device-detect';
import { useLoading } from '../LoadingContext';
import Lottie from "lottie-react";
import animationData from "../assets/voice-recognition.json"; // local JSON
// const animationData = process.env.REACT_APP_PUBLIC_URL + '/voice-recognition.json';
import DOMPurify from 'dompurify';

library.add(faMicrophone, faPaperPlane, faStop);

const MessageInput = forwardRef(({ onSendMessage, language, sessionId, placeholderText, messages, setSelectedOption, isTTSMuted, toggleTTSMute, isTTSGloballyEnabled, stopCurrentAudio }, ref) => {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const [placeholder, setPlaceholder] = useState(t('Type your message or use voice recording...'));
  const [isTyping, setIsTyping] = useState(false);
  const inputRef = useRef(null);
  const [isListening, setIsListening] = useState(false);
  const [recognition, setRecognition] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const recordingIntervalRef = useRef(null);
  const [isRecording, setIsRecording] = useState(false);
  const { setIsAPILoading } = useLoading();

  let timer;

  useEffect(() => {
    if ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window) {
      const recognition = new window.webkitSpeechRecognition();
      recognition.continuous = true;
      recognition.lang = language === 'en' ? 'en-US' : 'ar';
      let currentTranscript = '';
      recognition.onresult = async (event) => {
        currentTranscript = Array.from(event.results)
          .map((result) => result[0].transcript)
          .join('');

        if (currentTranscript.length <= 200) {
          handleSend(currentTranscript, true);
        }

        recognition.onend = () => {
          setIsRecording(false); // Ensure recording state is updated when recognition ends
        };
      };

      setRecognition(recognition);
    } else {
    }
  }, [language]);

  const startRecording = () => {
    if (recognition && !isListening) {
      // Stop any currently playing TTS audio
      if (stopCurrentAudio) {
        stopCurrentAudio();
      }
      recognition.start();
      setIsListening(true);
      setIsRecording(true);
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime((prevTime) => prevTime + 1);
      }, 1000);
    } else {
    }
  };

  const stopRecording = () => {
    if (recognition && isListening) {
      recognition.stop();
      setIsListening(false);
      clearInterval(recordingIntervalRef.current);
      setRecordingTime(0);
      setPlaceholder(t('Type your message or use voice recording...'));

      // Generate transcript and send the message
      const transcript = input; // Using the current input as the transcript
      if (transcript) {
        handleSend(transcript, true);
      }
    } else {
    }
  };

  // Check the microphone permission
  const checkMicrophonePermission = async () => {
    let audioStream = null;
    try {
      const permissionStatus = await navigator.permissions.query({ name: 'microphone' });

      if (permissionStatus.state === 'granted') {
      } else if (permissionStatus.state === 'denied') {
      } else {
        audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        audioStream.getTracks().forEach(track => track.stop());
      }

      permissionStatus.onchange = () => {
      };
    } catch (e) {
      console.error('Error checking microphone permission:', e);
    }
  };

  // Check the microphone permission on App load
  useEffect(() => {
    checkMicrophonePermission()
  }, [])

  const toggleListening = () => {
    try {
      if (recognition) {
        if (isListening) {
          stopRecording();
        } else {
          startRecording();
        }
      }
    } catch (e) {
    }
  };

  useEffect(() => {
    if (placeholderText) {
      setPlaceholder(placeholderText);
    }
  }, [placeholderText]);

  const trimHistoryAfterClear = (messages) => {
    const last10Messages = messages.slice(-10);
    const lastClearIndex = last10Messages.findIndex((msg) => msg.clearHistory || (msg && msg.formName === 'UpdateLeave'));
    return lastClearIndex !== -1 ? last10Messages.slice(lastClearIndex + 1) : last10Messages;
  };

  // Handle send message action
  const handleSend = async (text, recorded = false) => {
    setSelectedOption("services")
    text = typeof text === 'string' ? text : '';
    setInput(text);
    if ((input.trim() && input.length <= 200) || (text.trim() && text.length <= 200)) {
      const timestamp = new Date().getTime();
      onSendMessage({ sender: 'user', text: input ? input : text, timestamp });

      setInput('');
      setPlaceholder(t('Typing...'));
      setIsTyping(true);

      sessionStorage.setItem('clearHistory', 'false');
      let history = messages.length <= 1 ? JSON.parse(sessionStorage.getItem('history')) : messages;
      const trimmedMessages = trimHistoryAfterClear(history);
      const clonedMessages = trimmedMessages.map(msg => ({ ...msg }));
      clonedMessages.forEach(msg => {
        if (msg.formName === "EmployeesLeavesBalances" || msg.formName === "GetRelevantInfo" || msg.formName === "ReturnFromLeave" || msg.formName === "UpdateLeave") {
          msg.text = ''; // Set the text of the item to an empty string
          msg.leaveRequestData = '';
        }
      });

      try {
        let frontendLog = JSON.stringify({
          sessionID: sessionStorage.getItem('sessionID'),
          EmployeeID: localStorage.getItem('EmployeeID'),
          channel: isMobile ? 'Mobile' : 'Portal',
          language: localStorage.getItem('language'),
          isRecording: recorded,
          isAudioOn: localStorage.getItem("isTTSMuted") !== 'true'
        });
        setIsAPILoading(true);
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/chatbot/chat?code=${process.env.REACT_APP_CHATBOT_FUNCTION_KEY}`,
          {
            language: language,
            message: input ? DOMPurify.sanitize(input) : DOMPurify.sanitize(text),
            messages: sessionStorage.getItem('clearHistory') === 'true' ? [] : clonedMessages.slice(1),
            sessionId: sessionId,
            token: localStorage.getItem('id_token'),
            employeeID: localStorage.getItem('EmployeeID'),
            frontendLog: frontendLog,
          }
        );

        onSendMessage({
          sender: 'bot',
          text: response.data.message,
          isForm: response.data.isFormData,
          isLeaveRequest: response.data.isLeaveRequest,
          leaveRequestData: response.data.message,
          formName: response.data.formName,
          messageID: response.data.messageID,
          // title: response.data.title,
          custom: response?.data?.custom,
          timestamp: new Date().getTime()
        });
        setIsAPILoading(false);
      } catch (error) {
        onSendMessage({
          sender: 'bot',
          text: t('There was an error occurred, Please try again.'),
          timestamp: new Date().getTime(),
        });
        setIsAPILoading(false);
      } finally {
        setPlaceholder(t('Type your message or use voice recording...'));
        setIsTyping(false);
        setIsRecording(false);
        setIsAPILoading(false);
      }
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && input.trim() && input.length <= 200) {
      event.preventDefault();
      handleSend();
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
      .toString()
      .padStart(2, '0');
    const secs = (seconds % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  };

  return (
    <div className="message-input">
      <>
        {!isListening ? (
          <>
            <div className='chatbot-input-container'>
              <input
                type="text"
                ref={inputRef}
                value={input}
                onChange={(e) => {
                  if (e.target.value.length <= 200) {
                    setInput(e.target.value);
                  }
                }}
                className='chat-input'
                onKeyDown={handleKeyPress}
                placeholder={placeholder}
              />
              <div className={`loader ${isTyping ? '' : 'loader-none'}`} data-title="dot-typing">
                <div className="stage">
                  <div className="dot-typing"></div>
                </div>
              </div>
            </div>
          </>

        ) : (
          <div className="audio-visualizer">
            <div className="recording-time">
              {formatTime(recordingTime)}
            </div>
            <div className="audio-waves">
              {/* {[...Array(100)].map((_, index) => (
                <div key={index} className="wave"></div>
              ))} */}
              <Lottie animationData={animationData} loop={true} />
            </div>
          </div>
        )
        }
        {isTTSGloballyEnabled && (
          <button
            className={`tts-toggle-btn ${isTTSMuted ? 'muted' : ''}`}
            onClick={toggleTTSMute}
            title={isTTSMuted ? t('Unmute audio') : t('Mute audio')}
            aria-label={isTTSMuted ? t('Unmute audio') : t('Mute audio')}
          >
            <FontAwesomeIcon icon={isTTSMuted ? faVolumeMute : faVolumeUp} />
          </button>
        )}
        {!isListening && <button onClick={handleSend} className="btn-small">
          <FontAwesomeIcon icon="paper-plane" className="btn-send" />
        </button>}
        <button
          onTouchStart={toggleListening}
          onTouchEnd={toggleListening}
          className="btn-small"
          style={{ cursor: "pointer !important" }}
        >
          <FontAwesomeIcon icon="microphone" className={`btn-record ${isListening ? 'recording' : ''}`} />
        </button>
      </>
    </div>
  );
});

export default MessageInput;

